import { Flex, VStack } from '@chakra-ui/layout'
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { MLContext } from '../../../providers'
import AccessDeniedView from './AccessDeniedView'
import CollectionGridNew from './GridView/CollectionGridNew'
import NoFilesOrFoldersView from './NoFilesOrFoldersView'
import NotFoundView from './NotFoundView'
import EmptyMediaCollectionView from './EmptyMediaCollectionView'
import NoMediaCollectionsView from './NoMediaCollectionsView'
import CollectionsGridLoader from './GridView/CollectionGridLoader'
import MLGridLoader from './GridView/MLGridLoader'
import GridViewNew from './GridView/GridViewNew'

const CollectionsView = ({ scrollEl, minHeight }: { scrollEl: HTMLDivElement | null, minHeight: string }) => {
  const [
    {
      mediaCollectionList,
      folderList,
      fileList,
      recentFileFolderList,
      selectedFiles,
      selectedFolders,
      selectedMediaCollections,
      loadingFiles,
      loadingFolders,
      loadingMediaCollections,
      filePage,
      folderPage,
      mediaCollectionPage,
      recentMediaCollectionList,
      folderPath,
      viewableParent,
      loadingViewableParent,
      loadingFolderPermission,
      mediaCollectionRoute,
      currentMediaCollection,
    },
    dispatch,
  ] = useContext(MLContext)

  // ---------- Route detection (simple & robust) ----------
  const location = useLocation()
  const routeKey = location.pathname + location.search
  const prevRouteKeyRef = useRef(routeKey)
  const routeChanged = prevRouteKeyRef.current !== routeKey

  // Commit prev route AFTER this render so the render that observes change
  // also sees routeChanged === true.
  useLayoutEffect(() => {
    prevRouteKeyRef.current = routeKey
  }, [routeKey])

  // ---------- Debounced loader ----------
  const [showLoaderDebounced, setShowLoaderDebounced] = useState(false)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const anyLoading =
    (loadingFiles && filePage === 0) ||
    (loadingFolders && folderPage === 0) ||
    (loadingMediaCollections && mediaCollectionPage === 0) ||
    loadingViewableParent ||
    loadingFolderPermission

  // While loading => show loader immediately.
  // After loading stops => keep loader visible for a short delay.
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
      debounceTimeoutRef.current = null
    }

    if (anyLoading) {
      setShowLoaderDebounced(true)
    } else {
      debounceTimeoutRef.current = setTimeout(() => {
        setShowLoaderDebounced(false)
      }, 100)
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [anyLoading])

  // ---------- View selection ----------
  const childComponent = useMemo(() => {
    // Initial page loading gates for first page
    const isInitialPageLoading = currentMediaCollection
      ? (loadingFiles && filePage === 0) ||
        (loadingFolders && folderPage === 0) ||
        loadingViewableParent ||
        loadingFolderPermission
      : loadingMediaCollections && mediaCollectionPage === 0

    // Single guard: block empties + show loaders when any of these are true
    const shouldShowLoader = routeChanged || isInitialPageLoading || showLoaderDebounced

    if (currentMediaCollection) {
      if (shouldShowLoader) {
        return <MLGridLoader scrollEl={scrollEl} />
      }

      if (viewableParent === 'NOT_ALLOWED') {
        return <AccessDeniedView minHeight={minHeight} />
      }
      if (viewableParent === 'NOT_FOUND') {
        return <NotFoundView minHeight={minHeight} />
      }

      const isEmpty = folderList.length === 0 && fileList.length === 0
      if (isEmpty) {
        return folderPath === '/' ? (
          <EmptyMediaCollectionView minHeight={minHeight} />
        ) : (
          <NoFilesOrFoldersView minHeight={minHeight} />
        )
      }

      return (
        <VStack alignItems="flex-start" height="100%" spacing={8}>
          <GridViewNew
            scrollEl={scrollEl}
            dispatch={dispatch}
            fileList={fileList}
            folderList={folderList}
            recentFileFolderList={recentFileFolderList}
            mediaCollectionRoute={mediaCollectionRoute ?? false}
            currentMediaCollection={currentMediaCollection ?? ''}
            selectedFiles={selectedFiles}
            selectedFolders={selectedFolders}
            loadingFiles={loadingFiles}
            loadingFolders={loadingFolders}
            filePage={filePage}
            folderPage={folderPage}
            folderPath={folderPath}
            minHeight={minHeight}
          />
        </VStack>
      )
    }

    // Collections root (no current media collection)
    if (shouldShowLoader) {
      return <CollectionsGridLoader scrollEl={scrollEl} />
    }

    const collectionsEmpty =
      mediaCollectionList.length === 0 &&
      recentMediaCollectionList.length === 0 &&
      folderPath === '/'

    if (collectionsEmpty) {
      return <NoMediaCollectionsView minHeight={minHeight} />
    }

    return (
      <VStack alignItems="flex-start" height="100%" spacing={8}>
        <CollectionGridNew
          scrollEl={scrollEl}
          dispatch={dispatch}
          mediaCollectionList={mediaCollectionList}
          recentMediaCollectionList={recentMediaCollectionList}
          selectedMediaCollections={selectedMediaCollections}
          loadingMediaCollections={loadingMediaCollections}
          mediaCollectionPage={mediaCollectionPage}
          minHeight={minHeight}
        />
      </VStack>
    )
  }, [
    // guards
    routeChanged,
    showLoaderDebounced,

    // context
    mediaCollectionList,
    folderList,
    fileList,
    recentFileFolderList,
    selectedFiles,
    selectedFolders,
    selectedMediaCollections,
    loadingFiles,
    loadingFolders,
    loadingMediaCollections,
    filePage,
    folderPage,
    mediaCollectionPage,
    folderPath,
    viewableParent,
    loadingViewableParent,
    loadingFolderPermission,
    mediaCollectionRoute,
    currentMediaCollection,
    recentMediaCollectionList,
  ])

  return (
    <Flex height="100%" direction="column">
      {childComponent}
    </Flex>
  )
}

export default CollectionsView
